import dayjs from "dayjs";

export const INITIAL_LEAD_FORM = {
  name: "",
  phone: "",
  email: "",
  trek: "",
  start_date: null,
  end_date: null,
  persons: 1,
  estimated_value: "",
  status: "NEW",
  source: "WEBSITE",
  lead_type: "B2C",
  sales_agent: "",
  operator: "",
  notes: "",
};

const CURRENCY_SYMBOLS = { INR: "₹", USD: "$", NPR: "NRs", EUR: "€" };

const START_DATE_KEYS = [
  "start_date",
  "activity_start_date",
  "trek_start_date",
  "travel_start_date",
  "plan_start_date",
];
const END_DATE_KEYS = [
  "end_date",
  "activity_end_date",
  "trek_end_date",
  "travel_end_date",
  "plan_end_date",
];

const toPickerDate = (value) => {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.startOf("day").toDate() : null;
};

const pickLeadDate = (source, keys) => {
  if (!source) return null;
  for (const key of keys) {
    const parsed = toPickerDate(source[key]);
    if (parsed) return parsed;
  }
  return null;
};

export const unwrapLead = (raw) => {
  if (!raw || typeof raw !== "object") return {};
  const nested = raw.lead ?? raw.data;
  return nested && typeof nested === "object" && !Array.isArray(nested)
    ? { ...raw, ...nested }
    : raw;
};

export const planTitle = (p) => p.title || p.plan_name || p.name || `Plan #${p.id}`;

export const planMeta = (p) => {
  const currency = p.currency || p.currency_code || "";
  const price = p.price ?? p.base_price;
  const scope = p.cover_scope || p.cover_type;
  const parts = [];

  if (price != null && price !== "") {
    const sym = CURRENCY_SYMBOLS[currency] ?? (currency ? `${currency} ` : "");
    parts.push(`${sym}${Number(price).toLocaleString("en-IN")}${currency ? ` ${currency}` : ""}`);
  }
  if (scope) parts.push(scope);

  return parts.join(" · ");
};

export const leadToFormValues = (lead) => {
  if (!lead?.id) return { ...INITIAL_LEAD_FORM };
  return {
    name: lead.name || "",
    phone: lead.phone ?? "",
    email: lead.email || "",
    trek: lead.trek || lead.plan_name || lead.activity || "",
    start_date: pickLeadDate(lead, START_DATE_KEYS),
    end_date: pickLeadDate(lead, END_DATE_KEYS),
    persons: lead.persons ?? 1,
    estimated_value: lead.estimated_value ?? "",
    status: lead.status || "NEW",
    source: lead.source || "WEBSITE",
    lead_type: lead.lead_type || "B2C",
    sales_agent: lead.sales_agent ?? "",
    operator: lead.operator ?? "",
    notes: lead.notes || "",
  };
};

const normalizePhone = (phone) => String(phone ?? "").replace(/\D/g, "");

export const isLeadFormComplete = (form, activityId) => {
  if (!form?.name?.trim() || !form?.email?.trim() || !form?.lead_type || !activityId) {
    return false;
  }
  if (normalizePhone(form.phone).length < 10) return false;
  if (!form.start_date || !form.end_date) return false;
  return Number(form.persons || 0) >= 1;
};

export const buildQuotationPayload = (leadId, payload, activityId) => {
  const persons = Number(payload.persons || 1);
  return {
    quotation_type: payload.lead_type || "B2C",
    lead: leadId,
    customer_name: payload.name,
    phone: String(payload.phone),
    email: payload.email,
    cover: Number(activityId),
    plan_name: payload.trek,
    trek: payload.trek,
    persons,
    traveller_ages: Array.from({ length: persons }, () => 30),
    nationality: "Indian",
    travelling_from: "India",
    travelling_to: "India",
    notes: payload.notes || "",
  };
};

export const buildLeadPayload = (form, activityId) => ({
  ...form,
  start_date: form.start_date ? dayjs(form.start_date).format("YYYY-MM-DD") : null,
  end_date: form.end_date ? dayjs(form.end_date).format("YYYY-MM-DD") : null,
  persons: Number(form.persons || 0),
  sales_agent: form.sales_agent ? Number(form.sales_agent) : null,
  operator: form.operator ? Number(form.operator) : null,
  ...(activityId ? { cover_id: Number(activityId) } : {}),
});

export const planIdFromLead = (lead, plans = []) => {
  const cover = lead?.cover;
  const id =
    lead?.cover_id ??
    (cover && typeof cover === "object" ? cover.id : cover) ??
    lead?.activity_id ??
    lead?.customize_cover_id ??
    lead?.customize_cover?.id ??
    lead?.plan_id;
  if (id != null && id !== "") return String(id);
  const trek = String(lead?.trek || lead?.plan_name || lead?.activity || "").trim().toLowerCase();
  if (!trek || !plans.length) return "";
  const match = plans.find((p) =>
    [planTitle(p), p.title, p.plan_name, p.name]
      .filter(Boolean)
      .some((name) => String(name).trim().toLowerCase() === trek)
  );
  return match ? String(match.id) : "";
};
